import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { FileText, RefreshCw, ExternalLink, Plus, Receipt, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  open: "bg-blue-100 text-blue-700 border-blue-200",
  draft: "bg-amber-100 text-amber-700 border-amber-200",
  void: "bg-slate-100 text-slate-600 border-slate-200",
  uncollectible: "bg-red-100 text-red-700 border-red-200",
  failed: "bg-red-100 text-red-700 border-red-200",
};

const gbp = (pence) => `£${((pence || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span data-testid={`invoice-status-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {status}
    </span>
  );
}

export default function AgencyBillingPanel() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ count: 0, totalPence: 0, outstandingPence: 0, paidPence: 0 });
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [form, setForm] = useState({ charityOrgId: "", amount: "", description: "", billingPeriod: "", daysUntilDue: "14" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, cli] = await Promise.all([
        apiClient.get("/agency/billing/charity-invoices"),
        apiClient.get("/agency/billing/charity-clients"),
      ]);
      setInvoices(inv.data?.invoices || []);
      setSummary(inv.data?.summary || { count: 0, totalPence: 0, outstandingPence: 0, paidPence: 0 });
      setCharities(cli.data?.charities || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load charity billing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const amountPence = Math.round(parseFloat(form.amount) * 100);
    if (!form.charityOrgId) return toast.error("Select a charity to bill");
    if (!amountPence || amountPence <= 0) return toast.error("Enter a valid amount");
    if (!form.description.trim()) return toast.error("Enter a description / billing reference");
    setSubmitting(true);
    try {
      const r = await apiClient.post("/agency/billing/charity-invoice", {
        charityOrgId: form.charityOrgId,
        amountPence,
        description: form.description.trim(),
        billingPeriod: form.billingPeriod.trim() || null,
        daysUntilDue: parseInt(form.daysUntilDue) || 14,
      });
      if (r.data?.stripeProvisioned) {
        toast.success("Invoice created and sent via Stripe");
      } else {
        toast.warning("Invoice saved as draft — Stripe not provisioned (add a live Stripe key to send it).");
      }
      setDialogOpen(false);
      setForm({ charityOrgId: "", amount: "", description: "", billingPeriod: "", daysUntilDue: "14" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      const r = await apiClient.post(`/agency/billing/charity-invoices/${id}/refresh`);
      if (r.data?.refreshed) toast.success(`Status synced: ${r.data.status}`);
      else toast.info(r.data?.reason || "Nothing to sync yet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="agency-billing-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-indigo-600" /> Charity Billing
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Invoice the charities you deliver campaigns for. Separate from donor giving and platform fees.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-charity-invoice-btn" className="gap-2">
              <Plus className="h-4 w-4" /> New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="charity-invoice-dialog">
            <DialogHeader>
              <DialogTitle>New charity invoice</DialogTitle>
              <DialogDescription>Bill a charity for delivered campaign services. A Stripe invoice will be sent when a live key is configured.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Charity</Label>
                <Select value={form.charityOrgId} onValueChange={(v) => setForm((f) => ({ ...f, charityOrgId: v }))}>
                  <SelectTrigger data-testid="invoice-charity-select"><SelectValue placeholder="Select a charity" /></SelectTrigger>
                  <SelectContent>
                    {charities.map((c) => (
                      <SelectItem key={c.charityOrgId} value={c.charityOrgId} data-testid={`charity-option-${c.charityOrgId}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount (GBP)</Label>
                  <Input data-testid="invoice-amount-input" type="number" min="0" step="0.01" placeholder="2500.00"
                    value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                </div>
                <div>
                  <Label>Due in (days)</Label>
                  <Input data-testid="invoice-days-input" type="number" min="1" value={form.daysUntilDue}
                    onChange={(e) => setForm((f) => ({ ...f, daysUntilDue: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Description / reference</Label>
                <Textarea data-testid="invoice-description-input" placeholder="e.g. March 2026 campaign delivery — 142 signups"
                  value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <Label>Billing period (optional)</Label>
                <Input data-testid="invoice-period-input" placeholder="2026-03"
                  value={form.billingPeriod} onChange={(e) => setForm((f) => ({ ...f, billingPeriod: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="invoice-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="invoice-submit-btn">
                {submitting ? "Creating…" : "Create & send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="billing-summary-count"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-muted-foreground">Invoices</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{summary.count}</CardContent></Card>
        <Card data-testid="billing-summary-total"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-muted-foreground">Total billed</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{gbp(summary.totalPence)}</CardContent></Card>
        <Card data-testid="billing-summary-outstanding"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-muted-foreground">Outstanding</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-amber-600">{gbp(summary.outstandingPence)}</CardContent></Card>
        <Card data-testid="billing-summary-paid"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-muted-foreground">Paid</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-emerald-600">{gbp(summary.paidPence)}</CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Invoices</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="billing-empty-state">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No charity invoices yet. Create one to bill a charity for delivered services.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                    <th className="py-2 pr-4">Charity</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Period</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b last:border-0" data-testid={`invoice-row-${inv.id}`}>
                      <td className="py-3 pr-4 font-medium">{inv.charityName}</td>
                      <td className="py-3 pr-4 max-w-[220px] truncate" title={inv.description}>{inv.description}</td>
                      <td className="py-3 pr-4 tabular-nums">{gbp(inv.amountPence)}</td>
                      <td className="py-3 pr-4">
                        <StatusBadge status={inv.status} />
                        {inv.stripeError && (
                          <span className="ml-1 inline-flex" title={inv.stripeError}>
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">{inv.billingPeriod || "—"}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center justify-end gap-2">
                          {inv.hostedInvoiceUrl && (
                            <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" data-testid={`invoice-link-${inv.id}`}>
                              <Button size="sm" variant="outline" className="gap-1"><ExternalLink className="h-3.5 w-3.5" /> View</Button>
                            </a>
                          )}
                          <Button size="sm" variant="ghost" disabled={refreshingId === inv.id || !inv.stripeInvoiceId}
                            onClick={() => refresh(inv.id)} data-testid={`invoice-refresh-${inv.id}`}>
                            <RefreshCw className={`h-3.5 w-3.5 ${refreshingId === inv.id ? "animate-spin" : ""}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
